import React, { useEffect, useState } from "react";
import {
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import { sampleTestBlockMessage } from "../../../constants/sncSampleGuard";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { SnCPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  SNC_TOOLTIP_BY_SCREEN,
  sncTooltipGroupKey,
  type SnCStandardTooltipGroup,
} from "../../../constants/SnCTooltipInformation";
import { useTooltipText } from "../../../tooltips/TooltipContext";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

const SnCCancellationPolicy = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const sncTipGroup = sncTooltipGroupKey("Cancellation Policy");
  const sncTipDefaults = SNC_TOOLTIP_BY_SCREEN["Cancellation Policy"] as SnCStandardTooltipGroup;
  const tipHeader = useTooltipText(sncTipGroup, "header", sncTipDefaults.header);
  const tipCode = useTooltipText(sncTipGroup, "code", sncTipDefaults.code);
  const tipName = useTooltipText(sncTipGroup, "name", sncTipDefaults.name);
  const tipActive = useTooltipText(sncTipGroup, "active", sncTipDefaults.active);
  const [cancellationPolicyCode, setCancellationPolicyCode] = useState<
    string | null
  >(null);
  const [cancellationPolicyName, setCancellationPolicyName] = useState<
    string | null
  >(null);
  const [cancellationPolicy, setCancellationPolicy] = useState<string | null>(
    null,
  );
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [codeErr, setCodeErr] = useState("");
  const [nameErr, setNameErr] = useState("");
  const [policyErr, setPolicyErr] = useState("");

  useEffect(() => {
    fieldValues && setItems(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const payload = {
    "Cancellation Policy Code": cancellationPolicyCode,
    "Cancellation Policy Name": cancellationPolicyName,
    "Cancellation Policy": cancellationPolicy,
  };

  const validateInput = (): boolean => {
    setCodeErr("");
    setNameErr("");
    setPolicyErr("");
    if (!cancellationPolicyCode) {
      setCodeErr(
        "Cancellation Policy Code" + validationMessages.sncMandatorySuffix,
      );
    }
    if (!cancellationPolicyName) {
      setNameErr(
        "Cancellation Policy Name" + validationMessages.sncMandatorySuffix,
      );
    }
    if (!cancellationPolicy) {
      setPolicyErr(
        "Cancellation Policy" + validationMessages.sncMandatorySuffix,
      );
    }
    if (
      !cancellationPolicyCode ||
      !cancellationPolicyName ||
      !cancellationPolicy
    ) {
      setAlertMessage(validationMessages.sncAllMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateString(
        cancellationPolicyCode,
        validationLength.sncLooseCode15,
        validationType.NAME,
      )
    ) {
      setCodeErr(validationMessages.sncLooseCode15);
      return false;
    }
    if (
      !validateString(
        cancellationPolicyName,
        validationLength.sncLooseName50,
        validationType.NAME,
      )
    ) {
      setNameErr(validationMessages.sncLooseName50);
      return false;
    }
    if (
      !validateString(
        cancellationPolicy,
        validationLength.sncCancellationPolicyDesc,
        validationType.NAME,
      )
    ) {
      setPolicyErr(validationMessages.sncCancellationPolicyDesc);
      return false;
    }
    const sampleBlock = sampleTestBlockMessage(cancellationPolicyCode, cancellationPolicyName, cancellationPolicy);
    if (sampleBlock) {
      setAlertMessage(sampleBlock);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  const updateItem = () => {
    if (!validateInput()) {
      return;
    }
    const otherItems = (items ?? []).filter(
      (item: any) => item.id !== selectedId,
    );
    if (
      checkDuplicate(
        otherItems,
        "Cancellation Policy Code",
        cancellationPolicyCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${SnCPropertyEnum.cancellationPolicy} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        items ?? [],
        "Cancellation Policy Code",
        cancellationPolicyCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setCodeErr("");
      setNameErr("");
      setPolicyErr("");
      setCancellationPolicyCode(list["Cancellation Policy Code"] ?? null);
      setCancellationPolicyName(list["Cancellation Policy Name"] ?? null);
      setCancellationPolicy(list["Cancellation Policy"] ?? null);
    } else {
      setCancellationPolicyCode(null);
      setCancellationPolicyName(null);
      setCancellationPolicy(null);
      setCodeErr("");
      setNameErr("");
      setPolicyErr("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Cancellation Policy
            <Tooltip
              title={tooltipComponent(tipHeader)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px">
          <Stack direction="column">
            <FormLabel component="legend">
              Cancellation Policy Code
              <Tooltip title={tooltipComponent(tipCode)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              value={cancellationPolicyCode ?? ""}
              error={!!codeErr}
              helperText={codeErr}
              onChange={(e) => {
                setCancellationPolicyCode(e.target.value || null);
                setCodeErr("");
              }}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel>
              Cancellation Policy Name *
              <Tooltip title={tooltipComponent(tipName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              required
              variant="outlined"
              size="small"
              value={cancellationPolicyName ?? ""}
              error={!!nameErr}
              helperText={nameErr}
              onChange={(e) => {
                setCancellationPolicyName(e.target.value || null);
                setNameErr("");
              }}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel>
              Cancellation Policy *
              <Tooltip title={tooltipComponent(tipName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              required
              variant="outlined"
              size="small"
              multiline
              minRows={2}
              value={cancellationPolicy ?? ""}
              error={!!policyErr}
              helperText={policyErr}
              onChange={(e) => {
                setCancellationPolicy(e.target.value || null);
                setPolicyErr("");
              }}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Cancellation
            Policy
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Cancellation Policy Available</Typography>
          {items?.length ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code="Cancellation Policy Code"
              name="Cancellation Policy Name"
            />
          ) : (
            <Typography variant="body2">
              No Cancellation Policy Available
            </Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SnCCancellationPolicy;
